import { Callout, FormGroup, H1, Intent } from "@blueprintjs/core";
import {
  Button,
  Flex,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  VStack,
} from "@chakra-ui/react";
import coverPNG from "assets/placeholder.png";
import { LanguageSelector } from "components/ui";
import { useAuth } from "contexts/AuthProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { RiEye2Line, RiEyeCloseFill } from "react-icons/ri";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";

import styles from "./scss/Login.module.scss";

type LocationProps = {
  state: {
    from: Location;
  };
};

interface LoginPageProps {
  onLocaleChange?: (locale: string) => void;
}

const LoginPage = ({ onLocaleChange }: LoginPageProps) => {
  const navigate = useNavigate();
  const location = useLocation() as unknown as LocationProps;
  const { t } = useTranslation();
  const { jwtToken, signin, error, loading } = useAuth();
  const [show, setShow] = useState(false);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const from = location.state?.from?.pathname || "/";

  useEffect(() => {
    if (jwtToken) {
      // THIS WON'T EVER RUN AS jwtToken is not a state and is not getting updated ever
      navigate(from, { replace: true });
    }
  }, [jwtToken]);

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const formData = new FormData(event.currentTarget);
    const username = formData.get("username") as string;
    const password = formData.get("password") as string;

    signin(username, password, () => {
      if (!error) {
        localStorage.removeItem("getSessionStorage");
        navigate(from, { replace: true });
      } // Send them back to the page they tried to visit when they were
      // redirected to the login page. Use { replace: true } so we don't create
      // another entry in the history stack for the login page.  This means that
      // when they get to the protected page and click the back button, they
      // won't end up back on the login page, which is also really nice for the
      // user experience.
    });
  }

  useEffect(() => {
    window.addEventListener("storage", (event) => {
      console.log(event);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
      const jwtToken: string = sessionStorage.getItem("jwtToken");
      if (jwtToken) navigate(from, { replace: true });

      // When local storage changes, dump the list to
      // the console.
      if (event.key === "logout") {
        console.warn("[AUTH] logged out from storage!");
        navigate("/login", { replace: true });
      }
    });
  }, []);

  const onLanguageSelect = (e: React.FormEvent<HTMLSelectElement>): void => {
    const {
      currentTarget: { value },
    } = e;

    if (!onLocaleChange) return;
    onLocaleChange(value);
  };

  return (
    <div className={styles["with-sidebar"]}>
      <div className={styles.login}>
        <Flex bg="white" justify="center" align="center" px="16" py="12" rounded="lg" shadow="lg">
          <div>
            {from && <H1>{t("Log in")}</H1>}
            {error && <Callout intent={Intent.DANGER}>{t("Si è verificato un errore")}</Callout>}

            {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
            <form onSubmit={handleSubmit} className={styles.login__form}>
              <VStack align="flex-start">
                <FormGroup
                  helperText=""
                  label={t("Username/email")}
                  labelFor="username"
                  labelInfo="(required)"
                >
                  <Input id="username" name="username" size="md" />
                </FormGroup>
                <FormGroup
                  helperText=""
                  label={t("Password")}
                  labelFor="password"
                  labelInfo="(required)"
                >
                  <InputGroup size="md">
                    <Input
                      pr="4.5rem"
                      type={show ? "text" : "password"}
                      id="password"
                      name="password"
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label="show"
                        icon={show ? <RiEye2Line /> : <RiEyeCloseFill />}
                        size="sm"
                        variant="ghost"
                        onClick={() => setShow((prevState) => !prevState)}
                      >
                        {show ? "Hide" : "Show"}
                      </IconButton>
                    </InputRightElement>
                  </InputGroup>
                </FormGroup>
                <Link as={RouterLink} to="/recover-password" color="primaryFuchsia">
                  {t("Forgot your password?")}
                </Link>
                <Button isLoading={loading} type="submit" my={2} variant="primary">
                  {t("Log in")}
                </Button>
                <Flex pt={8} alignSelf="flex-start">
                  <LanguageSelector onLanguageSelect={onLanguageSelect} />
                </Flex>
              </VStack>
            </form>
          </div>
        </Flex>
      </div>
      <div className={styles.placeholder}>
        <img src={coverPNG} alt="" className={styles.placeholder__illustration} />
      </div>
    </div>
  );
};

export default LoginPage;
